import { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import {
  Button,
  Flex,
  Anchor,
  Group,
  MultiSelect,
  NumberInput,
  Table,
  TextInput,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { TimePicker } from "@mantine/dates";
import styled from "styled-components";
import ScrollBox from "../common/Table/ScrollBox";
import { getDepartmentSelectData } from "../../api/departmentApi";
import { ShiftDay } from "../../api/shiftDefinitionApi";
import { formatTerminalCode } from "../../utils/textUtils";
import {
  DAYS_OF_WEEK,
  endMinutesOf,
  hhmmToMinutes,
  minutesToHHMM,
  spansMidnight,
  weeklyWorkHours,
} from "../../utils/shiftPattern";

/**
 * Vardiya tanımı formu — YENİ ve DÜZENLE ekranlarının ikisi de bunu kullanır
 * (plan 2.24). Önceden `NewWorkShift.tsx` ve `WorkShift/UpdateForm.tsx` aynı
 * haftalık desen tablosunu iki ayrı kopyada tutuyordu; ölü `/weekly-shift`
 * uçlarından canlı `/shift-definition` uçlarına geçerken tek kopyaya indirildi.
 *
 * Form saatleri "HH:MM" + çalışma süresini SAAT olarak alır (İK'nın düşündüğü
 * birim), backend'e `startMinutes`/`workMinutes`/`breakMinutes` olarak gider.
 */

/** Formdaki tek gün satırı — boş bırakılan gün = o gün çalışılmıyor. */
type DayRow = {
  dayOfWeek: number;
  startTime: string;
  /** Saat cinsinden; ondalık olabilir (7.5). */
  workHours: number | "";
  breakMinutes: number | "";
};

export type ShiftDefinitionFormValues = {
  name: string;
  code: string;
  departmentIds: string[];
  dayCutoff: string;
  lateBufferMinutes: number | "";
  earlyLeaveBufferMinutes: number | "";
  notes: string;
  days: DayRow[];
};

const emptyRows = (): DayRow[] =>
  DAYS_OF_WEEK.map((d) => ({
    dayOfWeek: d.value,
    startTime: "",
    workHours: "",
    breakMinutes: "",
  }));

/** Kayıttaki desen → form satırları. Tanımlanmayan günler boş kalır. */
const rowsFromDays = (days: ShiftDay[] | undefined): DayRow[] =>
  DAYS_OF_WEEK.map((d) => {
    const match = days?.find((x) => x.dayOfWeek === d.value);
    return match
      ? {
          dayOfWeek: d.value,
          startTime: minutesToHHMM(match.startMinutes),
          workHours: Math.round((match.workMinutes / 60) * 100) / 100,
          breakMinutes: match.breakMinutes ?? 0,
        }
      : { dayOfWeek: d.value, startTime: "", workHours: "" as const, breakMinutes: "" as const };
  });

/** Bir satır dolduruldu mu (kısmen bile). */
const isRowTouched = (row: DayRow) =>
  Boolean(row.startTime) || row.workHours !== "" || row.breakMinutes !== "";

/** Form satırları → backend deseni. Yalnızca eksiksiz doldurulmuş günler gider. */
export const daysFromRows = (rows: DayRow[]): ShiftDay[] =>
  rows
    .filter((row) => row.startTime && row.workHours !== "" && Number(row.workHours) > 0)
    .map((row) => ({
      dayOfWeek: row.dayOfWeek,
      startMinutes: hhmmToMinutes(row.startTime) ?? 0,
      workMinutes: Math.round(Number(row.workHours) * 60),
      breakMinutes: row.breakMinutes === "" ? 0 : Number(row.breakMinutes),
    }));

type Props = {
  /** Düzenlemede mevcut kayıt; yeni kayıtta `undefined`. */
  initial?: {
    name?: string;
    code?: string;
    departmentIds?: string[];
    days?: ShiftDay[];
    dayCutoff?: string;
    lateBufferMinutes?: number;
    earlyLeaveBufferMinutes?: number;
    notes?: string;
  };
  submitLabel: string;
  onSubmit: (values: ShiftDefinitionFormValues) => Promise<void> | void;
  /** Sayfa başlığındaki butondan tetiklemek için (WorkShiftDetail deseni). */
  submitRef?: React.MutableRefObject<any>;
};

/**
 * Kesim saati denetimi — 2 Eylül'de sahada yanlış anlaşıldı.
 *
 * Kullanıcı "gün kesim saati"ni günün BİTİŞİ sanıp 10:30–17:30 çalışan bir
 * vardiyaya 22:00 yazdı. Kod ise kesimi günün BAŞLANGICI olarak okuyor: kesimden
 * önceki her okutma bir ÖNCEKİ iş gününe yazılır. Sonuç: 2 Eylül'de yapılan
 * bütün giriş/çıkışlar 1 Eylül iş gününe düştü ve çalışan uygulamada kendini
 * "dışarıda" gördü. Kural tek cümle: **kesim, vardiya başlangıcından önce
 * olmalı**; yoksa mesai kendi gününe yazılmaz.
 */
/**
 * Kesim saatinin GEÇERLİ ARALIĞI: `(geceBitisi, enErkenBaslangic]`.
 *
 * Kural iki uçlu:
 *   - Kesim, vardiya başlangıcından SONRA olamaz → yoksa o mesai bir önceki
 *     iş gününe yazılır (2 Eyl'de sahada yaşandı: 10:30 mesaiye 22:00 kesim).
 *   - Gece yarısını aşan bir vardiya varsa, kesim o vardiyanın sabaha sarkan
 *     bitişinden ÖNCE olamaz → yoksa tek gece iki iş gününe bölünür.
 *
 * Aralık, vardiyanın 24 saatlik daire üzerinde BOŞ bıraktığı yerdir; "gündüz mü
 * gece mi" gibi ikili bir tahminle bulunamaz. 01:00'de başlayan bir vardiyada
 * eski ikili mantık 04:00 öneriyordu — mesainin tam ortası (kullanıcı bildirdi).
 */
export const kesimAraligi = (
  rows: DayRow[]
): { alt: number; ust: number } | null => {
  const gunler = daysFromRows(rows);
  if (!gunler.length) return null;

  const enErkenBaslangic = Math.min(...gunler.map((g) => g.startMinutes));
  // Sabaha sarkan bitişlerin en GEÇİ; hiç yoksa 0 (gece boş).
  const geceBitisi = gunler
    .filter((g) => spansMidnight(g))
    .reduce((enGec, g) => Math.max(enGec, endMinutesOf(g) - 1440), 0);

  if (geceBitisi >= enErkenBaslangic) return null; // boşluk yok
  return { alt: geceBitisi, ust: enErkenBaslangic };
};

/** Aralığın içinden makul bir saat: önce alışılmış değerler, olmazsa ortası. */
const kesimOnerisi = (aralik: { alt: number; ust: number }): string => {
  const icinde = (dk: number) => dk > aralik.alt && dk <= aralik.ust;
  for (const alisilmis of [4 * 60, 12 * 60, 0]) {
    if (icinde(alisilmis)) return minutesToHHMM(alisilmis);
  }
  // Yarım saate yuvarlanmış orta nokta; aralığın dışına taşmayacak şekilde.
  const orta = Math.floor((aralik.alt + aralik.ust) / 2 / 30) * 30;
  return minutesToHHMM(icinde(orta) ? orta : aralik.ust);
};

export const kesimUyarisi = (
  dayCutoff: string,
  rows: DayRow[]
): { mesaj: string; oneri: string } | null => {
  const kesim = hhmmToMinutes(dayCutoff);
  if (kesim == null) return null;

  const aralik = kesimAraligi(rows);
  if (!aralik) return null;
  if (kesim > aralik.alt && kesim <= aralik.ust) return null; // geçerli

  const oneri = kesimOnerisi(aralik);
  const mesaj =
    kesim > aralik.ust
      ? `Kesim, vardiya başlangıcından (${minutesToHHMM(
          aralik.ust
        )}) sonra olamaz — o mesai bir ÖNCEKİ iş gününe yazılır.`
      : `Kesim, gece vardiyasının bitişinden (${minutesToHHMM(
          aralik.alt
        )}) önce olamaz — tek gece iki iş gününe bölünür.`;

  return { mesaj, oneri };
};

const ShiftDefinitionForm = ({ initial, submitLabel, onSubmit, submitRef }: Props) => {
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  const form = useForm<ShiftDefinitionFormValues>({
    initialValues: {
      name: initial?.name ?? "",
      code: initial?.code ?? "",
      departmentIds: initial?.departmentIds ?? [],
      dayCutoff: initial?.dayCutoff ?? "",
      lateBufferMinutes: initial?.lateBufferMinutes ?? "",
      earlyLeaveBufferMinutes: initial?.earlyLeaveBufferMinutes ?? "",
      notes: initial?.notes ?? "",
      days: initial?.days ? rowsFromDays(initial.days) : emptyRows(),
    },
    validate: {
      name: (v) => (v.trim() ? null : "Vardiya adı zorunludur."),
      code: (v) => (v.trim() ? null : "Vardiya kodu zorunludur."),
      dayCutoff: (v) =>
        !v || hhmmToMinutes(v) != null ? null : "Kesim saati 'HH:MM' olmalı.",
      days: (rows) => {
        if (daysFromRows(rows).length === 0) {
          return "En az bir gün için giriş saati ve çalışma süresi girilmeli.";
        }
        // Yarım doldurulmuş satır sessizce atılırsa İK "girdim" sanır.
        const halfFilled = rows.some(
          (row) => isRowTouched(row) && (!row.startTime || !row.workHours)
        );
        return halfFilled
          ? "Doldurulan günlerde hem giriş saati hem çalışma süresi olmalı."
          : null;
      },
    },
  });

  const kesimUyari = kesimUyarisi(form.values.dayCutoff, form.values.days);

  useEffect(() => {
    getDepartmentSelectData()
      .then(({ data }: any) =>
        setDepartmentOptions(
          (data?.data ?? []).map((d: any) => ({
            value: String(d.value),
            label: d.label,
          }))
        )
      )
      .catch(() => setDepartmentOptions([]));
  }, []);

  const handleSubmit = async (values: ShiftDefinitionFormValues) => {
    setSaving(true);
    try {
      await onSubmit(values);
    } finally {
      setSaving(false);
    }
  };

  const totalHours = weeklyWorkHours(daysFromRows(form.values.days));

  return (
    <form ref={submitRef} onSubmit={form.onSubmit(handleSubmit)}>
      <Flex gap={15} mb={15} wrap="wrap">
        <TextInput label="Vardiya Adı" w={240} required {...form.getInputProps("name")} />
        <TextInput
          label="Vardiya Kodu"
          w={160}
          required
          {...form.getInputProps("code")}
          onChange={(event) =>
            form.setFieldValue("code", formatTerminalCode(event.target.value))
          }
        />
        <MultiSelect
          label="Departmanlar"
          w={280}
          data={departmentOptions}
          placeholder={
            form.values.departmentIds.length === 0 ? "Tüm şirket" : undefined
          }
          description="Boş bırakılırsa vardiya şirket geneli olur."
          {...form.getInputProps("departmentIds")}
        />
      </Flex>

      <Flex gap={15} mb={20} wrap="wrap" align="flex-start">
        <Flex direction="column" w={240} gap={4}>
          <TimePicker
            /*
             * Açıklama kalıcı `description` olarak dururken girdiyi 63 px aşağı
             * itiyor ve satırdaki diğer iki alanla hizası bozuluyordu (ölçüldü).
             * Metin etiketin yanındaki bilgi simgesine taşındı; asıl koruma
             * zaten aşağıdaki uyarı, tooltip yalnızca referans.
             */
            label={
              <Group gap={4} align="center" wrap="nowrap">
                <span>Gün Kesim Saati</span>
                <Tooltip
                  multiline
                  w={300}
                  withArrow
                  withinPortal
                  events={{ hover: true, focus: true, touch: true }}
                  label="İş gününün BAŞLADIĞI saat. Bu saatten önceki okutmalar bir önceki iş gününe yazılır — yani vardiya başlangıcından önce olmalı. Gündüz vardiyasında 04:00, gece vardiyasında 12:00 tipiktir. Boş bırakılırsa 04:00 kullanılır."
                >
                  <IconInfoCircle
                    size={15}
                    style={{ cursor: "help", opacity: 0.55 }}
                  />
                </Tooltip>
              </Group>
            }
            {...form.getInputProps("dayCutoff")}
            // ⚠ `error` SPREAD'DEN SONRA gelmeli: `getInputProps` kendi
            // `error: undefined`'ını yayıyor ve önce yazılırsa uyarıyı eziyor
            // (ilk denemede uyarı hiç görünmedi).
            error={form.errors.dayCutoff || kesimUyari?.mesaj}
          />
          {kesimUyari && (
            <Anchor
              size="sm"
              onClick={() => form.setFieldValue("dayCutoff", kesimUyari.oneri)}
            >
              Önerilen: {kesimUyari.oneri} — uygula
            </Anchor>
          )}
        </Flex>
        <NumberInput
          label="Geç Kalma Toleransı (dk)"
          w={190}
          min={0}
          step={5}
          {...form.getInputProps("lateBufferMinutes")}
        />
        <NumberInput
          label="Erken Çıkma Toleransı (dk)"
          w={190}
          min={0}
          step={5}
          {...form.getInputProps("earlyLeaveBufferMinutes")}
        />
      </Flex>

      <ScrollBox $minWidth={640}>
        <Table verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <StyledTh style={{ width: "16%" }}>Gün</StyledTh>
              <StyledTh style={{ width: "18%" }}>Giriş Saati</StyledTh>
              <StyledTh style={{ width: "20%" }}>Çalışma Süresi (Saat)</StyledTh>
              <StyledTh style={{ width: "20%" }}>Mola Süresi (Dakika)</StyledTh>
              <StyledTh style={{ width: "18%" }}>Çıkış Saati</StyledTh>
              <StyledTh style={{ width: "8%" }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {form.values.days.map((row, index) => {
              const startMinutes = hhmmToMinutes(row.startTime);
              const complete = startMinutes != null && row.workHours !== "";
              const day = complete
                ? {
                    startMinutes,
                    workMinutes: Math.round(Number(row.workHours) * 60),
                    breakMinutes: row.breakMinutes === "" ? 0 : Number(row.breakMinutes),
                  }
                : null;

              return (
                <Table.Tr key={row.dayOfWeek}>
                  <Table.Td style={{ fontSize: 16 }}>
                    {DAYS_OF_WEEK[index].label}
                  </Table.Td>
                  <Table.Td>
                    <TimePicker {...form.getInputProps(`days.${index}.startTime`)} />
                  </Table.Td>
                  <Table.Td>
                    <NumberInput
                      min={0}
                      max={24}
                      step={0.5}
                      decimalScale={2}
                      {...form.getInputProps(`days.${index}.workHours`)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <NumberInput
                      min={0}
                      step={5}
                      {...form.getInputProps(`days.${index}.breakMinutes`)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TimePicker
                      readOnly
                      value={day ? minutesToHHMM(endMinutesOf(day)) : ""}
                      rightSectionWidth={90}
                      rightSection={
                        day && spansMidnight(day) ? (
                          <NextDayHint>ertesi gün</NextDayHint>
                        ) : null
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <Button
                      color="red"
                      variant="light"
                      disabled={!isRowTouched(row)}
                      onClick={() =>
                        form.setFieldValue(`days.${index}`, {
                          dayOfWeek: row.dayOfWeek,
                          startTime: "",
                          workHours: "",
                          breakMinutes: "",
                        })
                      }
                    >
                      Temizle
                    </Button>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollBox>

      {form.errors.days && <ErrorText>{form.errors.days}</ErrorText>}

      <Flex justify="space-between" align="flex-end" mt="md" gap={20} wrap="wrap">
        <Textarea
          label="Not"
          w={360}
          autosize
          minRows={2}
          {...form.getInputProps("notes")}
        />
        <Flex direction="column" align="flex-end" gap={8}>
          <TotalHours>Haftalık toplam: {totalHours} saat</TotalHours>
          <Button color="#5c26ff" type="submit" loading={saving}>
            {submitLabel}
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};

export default ShiftDefinitionForm;

const StyledTh = styled(Table.Td)`
  color: #4c4c4c;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
`;

const NextDayHint = styled.span`
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
`;

const ErrorText = styled.div`
  color: #e03131;
  font-size: 13px;
  margin-top: 8px;
`;

const TotalHours = styled.div`
  color: #4c4c4c;
  font-size: 14px;
`;
